import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { apiFetch } from '@/lib/http/client';
import type {
  AttendeesResponse,
  AttendeeView,
  EnrichmentStatus,
} from '@/lib/recordings/types/attendees';
import { displayNameFor } from './attendee-display';

// ── Suggestion shape (flattened, for the speaker-label dropdown + transcript) ──

export type AttendeeSuggestion = {
  email: string;
  name?: string;
  photoUrl?: string;
  /** Carried so consumers can apply the Gravatar fallback for enriched rows. */
  enrichmentStatus?: EnrichmentStatus;
};

/**
 * Flatten raw `AttendeeView` rows into dropdown suggestions. `name` uses the
 * same fallback the sidebar uses (Apollo name → calendar displayName); the raw
 * email is returned as just `{ email }` so the email-on-top render path kicks in.
 */
export function toAttendeeSuggestions(attendees: AttendeeView[]): AttendeeSuggestion[] {
  return attendees.map((a) => {
    const resolved = displayNameFor(a);
    const base: AttendeeSuggestion =
      resolved === a.email
        ? { email: a.email, enrichmentStatus: a.enrichmentStatus }
        : { email: a.email, name: resolved, enrichmentStatus: a.enrichmentStatus };
    return a.photoUrl ? { ...base, photoUrl: a.photoUrl } : base;
  });
}

// ── Module cache + invalidation pub/sub ────────────────────────────────────────
//
// Single source of truth for a roster scope's attendees, shared by every
// consumer (panel, hover card, transcript reader, speaker-label editor). One
// fetch per scope per page session; mutations invalidate and every mounted
// hook refetches.
//
// A scope is (entityId, surfaceId): a surface's roster is its own linked
// event's invitees union its own manual rows, distinct from the document-level
// roster (surfaceId omitted) and from any other surface on the same document.

const cache = new Map<string, Promise<AttendeeView[]>>();
const versions = new Map<string, number>();
const listeners = new Set<() => void>();

/** Cache key for a roster scope. A surface has its own roster; omitting
 *  surfaceId is the document-level default. */
function scopeKey(entityId: string, surfaceId?: string | null): string {
  return surfaceId ? `${entityId}::${surfaceId}` : entityId;
}

function attendeesUrl(entityId: string, surfaceId?: string | null): string {
  return surfaceId
    ? `/entities/${entityId}/attendees?surfaceId=${encodeURIComponent(surfaceId)}`
    : `/entities/${entityId}/attendees`;
}

function versionOf(key: string): number {
  return versions.get(key) ?? 0;
}

/** Drop the cached attendees for a scope and notify mounted hooks to refetch.
 * Call after any server-side attendee add/remove. */
export function invalidateEntityAttendees(entityId: string, surfaceId?: string | null): void {
  const key = scopeKey(entityId, surfaceId);
  cache.delete(key);
  versions.set(key, versionOf(key) + 1);
  listeners.forEach((l) => l());
}

/** Subscribe to attendee-cache invalidations (any scope). */
export function subscribeEntityAttendees(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Current invalidation version for a scope (bumps on invalidate). */
export function entityAttendeesVersion(
  entityId: string | null,
  surfaceId?: string | null,
): number {
  return entityId ? versionOf(scopeKey(entityId, surfaceId)) : 0;
}

/** Test-only: clear the module cache + versions between specs. */
export function resetEntityAttendeesCacheForTests(): void {
  cache.clear();
  versions.clear();
}

function fetchAttendees(entityId: string, surfaceId?: string | null): Promise<AttendeeView[]> {
  const key = scopeKey(entityId, surfaceId);
  let promise = cache.get(key);
  if (!promise) {
    promise = apiFetch<AttendeesResponse>(attendeesUrl(entityId, surfaceId))
      .then((r) => (r && Array.isArray(r.attendees) ? r.attendees : []))
      .catch((err: unknown) => {
        // Don't poison the cache on failure — drop so a future read retries.
        cache.delete(key);
        throw err;
      });
    cache.set(key, promise);
  }
  return promise;
}

// ── Hook ────────────────────────────────────────────────────────────────────

export type UseEntityAttendeesResult = {
  /** Raw attendee rows (full enrichment fields). */
  attendees: AttendeeView[];
  /** Flattened `{ email, name?, photoUrl?, enrichmentStatus? }` for dropdowns. */
  suggestions: AttendeeSuggestion[];
  loading: boolean;
  error: string | null;
  /** Force a fresh fetch (bypasses + repopulates the cache). */
  refetch: () => Promise<void>;
  /** Pessimistic add — POSTs, then invalidates so all consumers refetch. */
  addAttendee: (email: string) => Promise<AttendeeView>;
  /** Optimistic remove — drops locally, DELETEs, then invalidates. */
  removeAttendee: (email: string) => Promise<void>;
};

/**
 * Single source of truth for a roster scope's attendees. Module-cached per
 * (entityId, surfaceId) scope (one fetch shared across all mounts of that
 * scope) with an invalidation pub/sub so add/remove anywhere refreshes every
 * consumer of that scope.
 *
 * `surfaceId` is optional: omitting it (undefined or null) reproduces the
 * document-level roster. Idle when `entityId` is null or `enabled` is false.
 */
export function useEntityAttendees(
  entityId: string | null,
  surfaceId?: string | null,
  opts?: { enabled?: boolean },
): UseEntityAttendeesResult {
  const enabled = opts?.enabled ?? true;
  const [attendees, setAttendees] = useState<AttendeeView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attendeesRef = useRef<AttendeeView[]>([]);
  attendeesRef.current = attendees;

  const version = useSyncExternalStore(
    subscribeEntityAttendees,
    () => entityAttendeesVersion(entityId, surfaceId),
    () => 0,
  );

  useEffect(() => {
    if (!entityId || !enabled) {
      setAttendees([]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchAttendees(entityId, surfaceId)
      .then((list) => {
        if (cancelled) return;
        setAttendees(list);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setAttendees([]);
        setError(err instanceof Error ? err.message : String(err) || 'fetch failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `version` bumps on invalidate → re-run → cache miss → refetch.
  }, [entityId, surfaceId, enabled, version]);

  const suggestions = useMemo(() => toAttendeeSuggestions(attendees), [attendees]);

  const refetch = useCallback(async () => {
    if (!entityId) return;
    cache.delete(scopeKey(entityId, surfaceId));
    setLoading(true);
    try {
      const list = await fetchAttendees(entityId, surfaceId);
      setAttendees(list);
      setError(null);
    } catch (err) {
      setAttendees([]);
      setError(err instanceof Error ? err.message : String(err) || 'fetch failed');
    } finally {
      setLoading(false);
    }
  }, [entityId, surfaceId]);

  const addAttendee = useCallback(
    async (email: string): Promise<AttendeeView> => {
      if (!entityId) throw new Error('cannot add attendee without entityId');
      const created = (await apiFetch(attendeesUrl(entityId, surfaceId), {
        method: 'POST',
        body: JSON.stringify({ email }),
      })) as AttendeeView;
      setAttendees((prev) => [...prev, created]);
      invalidateEntityAttendees(entityId, surfaceId);
      return created;
    },
    [entityId, surfaceId],
  );

  const removeAttendee = useCallback(
    async (email: string): Promise<void> => {
      if (!entityId) throw new Error('cannot remove attendee without entityId');
      const snapshot = attendeesRef.current;
      setAttendees((prev) => prev.filter((a) => a.email !== email));
      try {
        const base = surfaceId
          ? `/entities/${entityId}/attendees/${encodeURIComponent(email)}?surfaceId=${encodeURIComponent(surfaceId)}`
          : `/entities/${entityId}/attendees/${encodeURIComponent(email)}`;
        await apiFetch(base, { method: 'DELETE' });
        invalidateEntityAttendees(entityId, surfaceId);
      } catch (e) {
        setAttendees(snapshot);
        throw e;
      }
    },
    [entityId, surfaceId],
  );

  return { attendees, suggestions, loading, error, refetch, addAttendee, removeAttendee };
}
